import React, { useContext, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from './UserContext';
import { getUser, loginUser, loginWithGoogle } from './apiService';
import GoogleSignInButton from './GoogleSignInButton';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckGlyph, checkBulletSx, sectionCardSx } from './designSystem';

const loginHighlights = [
  'Manage district baselines and classroom profiles',
  'Set allowlists, blocklists, and AI review rules',
  'Oversee devices, schedules, and staff permissions',
];

function Login() {
  const { setUser } = useContext(UserContext);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get('invite');

  const handleLogin = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const tokenResponse = await loginUser(username, password);
      const token = tokenResponse.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      const updatedUser = await getUser();
      setUser(updatedUser);
      navigate(inviteToken ? `/invite/${inviteToken}` : '/');
      window.location.reload();
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Unable to sign in with those credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credential) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const tokenResponse = await loginWithGoogle({
        credential,
        mode: 'signin',
      });
      const token = tokenResponse.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      const updatedUser = await getUser();
      setUser(updatedUser);
      navigate(inviteToken ? `/invite/${inviteToken}` : '/');
      window.location.reload();
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Unable to continue with Google.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      sx={{
        py: { xs: 5, md: 8 },
        minHeight: { md: 'calc(100vh - 220px)' },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ maxWidth: 1120, mx: 'auto', width: '100%' }}>
        <Card sx={{ ...sectionCardSx, overflow: 'hidden', borderRadius: '24px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' } }}>
            <Box
              sx={{
                position: 'relative',
                p: { xs: 4, md: 6 },
                background:
                  'radial-gradient(circle at 85% -10%, rgba(19,148,127,0.4), transparent 46%), radial-gradient(circle at -10% 110%, rgba(14,77,110,0.65), transparent 52%), linear-gradient(160deg, #0B2E43 0%, #11425F 100%)',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
                  backgroundSize: '44px 44px',
                  maskImage: 'radial-gradient(ellipse at 20% 20%, black 30%, transparent 78%)',
                  pointerEvents: 'none',
                }}
              />
              <Box sx={{ position: 'relative' }}>
                <Typography sx={{ color: '#7EE8D6', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2 }}>
                  Administrator Console
                </Typography>
                <Typography variant="h3" sx={{ mb: 2, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
                  School control center.
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.8, maxWidth: 440 }}>
                  For principals, IT teams, and pilot administrators managing web filtering across your district.
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 4 }}>
                  {loginHighlights.map((item) => (
                    <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{ ...checkBulletSx, bgcolor: 'rgba(19,148,127,0.24)', border: '1px solid rgba(19,148,127,0.45)' }}>
                        <CheckGlyph color="#7EE8D6" />
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>

            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
              <Box component="form" onSubmit={(event) => { event.preventDefault(); handleLogin(); }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#0B2E43' }}>Sign in</Typography>
                    <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                      Welcome back. Open your school workspace.
                    </Typography>
                  </Box>

                  {inviteToken && (
                    <Alert severity="info">
                      Sign in with the email address that received the school invitation to continue into that workspace.
                    </Alert>
                  )}

                  {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                  <TextField
                    label="Administrator username or email"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    autoComplete="username"
                    fullWidth
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    fullWidth
                  />
                  <Button type="submit" variant="contained" color="primary" size="large" disabled={loading}>
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>

                  <GoogleSignInButton
                    text="continue_with"
                    onCredential={handleGoogleLogin}
                    helperText="Use Google to sign in to an existing BlockWebAI administrator account."
                  />

                  <Typography sx={{ color: '#52677A', fontSize: '0.95rem' }}>
                    Need an administrator account?{' '}
                    <RouterLink to={inviteToken ? `/register?invite=${inviteToken}` : '/register'} style={{ color: '#0E4D6E', fontWeight: 700 }}>
                      Start a school pilot
                    </RouterLink>
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Box>
        </Card>
      </Box>
    </Container>
  );
}

export default Login;
